import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAllUsers } from "../../services/userService";
import { IUser } from "../../types";
import { Table } from "../../components/table/table";
import { LuDownload, LuEllipsisVertical, LuPlus } from "react-icons/lu";
import { BeatLoader } from "react-spinners";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { AddAdmin } from "../../components/modals/add-admin";
import { useQueryClient } from "@tanstack/react-query";

interface AllUsersProps {
  searchQuery?: string; // Accept searchQuery as a prop
}

const AllUsers: React.FC<AllUsersProps> = ({ searchQuery }) => {
  const queryClient = useQueryClient();

  const {
    data: users,
    isLoading,
    error,
  } = useQuery<IUser[]>({
    queryKey: ["allUsers", searchQuery], // Include searchQuery in the query key
    queryFn: () => getAllUsers(searchQuery), // Pass searchQuery to the service
  });

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openMenu &&
        !document
          .getElementById(`menu-${openMenu}`)
          ?.contains(event.target as Node)
      ) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenu]);

  const toggleMenu = (userId: string) => {
    setOpenMenu((prev) => (prev === userId ? null : userId));
  };

  const handleView = (id: string) => {
    setOpenMenu(null);
    navigate(`/users/${id}`, { state: { referrer: "/users/all" } });
  };

  console.log(users);

  const downloadCSV = () => {
    if (!users) return;

    const csvContent = [
      ["Name", "Email", "Role", "Email Verified", "Status"],
      ...users.map((user) => [
        `${user.firstName} ${user.lastName}`,
        user.email,
        user.role,
        user.emailVerified ? "Yes" : "No",
        user.userStatus,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "users.csv");
  };

  const columns = [
    {
      header: "Info",
      accessorKey: "info",
      cell: ({ row }: { row: { original: IUser } }) => (
        <div className="flex items-center space-x-2">
          <img
            src={row.original.image || "/default-avatar.png"}
            alt="User Avatar"
            className="w-8 h-8 rounded-full"
          />
          <div>
            <p className="font-medium text-gray-800">
              {row.original.firstName} {row.original.lastName}
            </p>
            <p className="text-sm text-gray-500">{row.original.email}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Role",
      accessorKey: "role",
      cell: ({ row }: { row: { original: IUser } }) => (
        <p className="capitalize text-sm text-gray-700">{row.original.role}</p>
      ),
    },
    {
      header: "Email Verified",
      accessorKey: "emailVerified",
      cell: ({ row }: { row: { original: IUser } }) => (
        <span
          className={`px-2 py-1  text-sm ${
            row.original.emailVerified ? "text-emerald-500" : "text-rose-500"
          }`}
        >
          {row.original.emailVerified ? "Yes" : "No"}
        </span>
      ),
    },
    {
      header: "Status",
      accessorKey: "userStatus",
      cell: ({ row }: { row: { original: IUser } }) => (
        <span
          className={`px-2 py-1  text-sm  capitalize ${
            row.original.userStatus === "activated"
              ? "text-emerald-500"
              : row.original.userStatus === "suspended"
              ? "text-rose-500"
              : "text-amber-500"
          }`}
        >
          {row.original.userStatus}
        </span>
      ),
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: IUser } }) => (
        <div className="relative">
          <button
            onClick={() => toggleMenu(row.original._id.toString())}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <LuEllipsisVerticalIcon className="text-gray-600 text-lg" />
          </button>

          {openMenu === row.original._id.toString() && (
            <div
              id={`menu-${row.original._id}`}
              className="absolute right-0 mt-2 w-32 bg-white shadow-lg rounded-md z-10"
            >
              <button
                onClick={() => handleView(row.original._id.toString())}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
              >
                View
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  const LuPlusIcon = LuPlus as React.ElementType;
  const LuDownloadIcon = LuDownload as React.ElementType;
  const LuEllipsisVerticalIcon = LuEllipsisVertical as React.ElementType;

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );

  if (error)
    return <p className="text-rose-500 text-sm">Error loading users.</p>;

  return (
    <div className="">
      <div className="px-2 py-4 md:px-4 md:py-2 flex items-center justify-between">
        <h2 className="text-xl">All Users</h2>
        <div className="flex items-center gap-2">
          <div
            onClick={downloadCSV}
            className="px-4 py-2 bg-transparent border flex items-center justify-between space-x-2 text-gray-800 text-sm rounded-full hover:opacity-50 cursor-pointer transition"
          >
            <LuDownloadIcon />
            <span>CSV</span>
          </div>
          <div
            onClick={() => setIsModalOpen(true)}
            className="p-2 cursor-pointer bg-gray-800 rounded-full flex items-center justify-center text-white hover:bg-opacity-50 transition"
          >
            <LuPlusIcon className="text-lg" />
          </div>
        </div>
      </div>
      <Table columns={columns} data={users || []} />
      {isModalOpen && (
        <AddAdmin
          onClose={() => setIsModalOpen(false)}
          onAdminAdded={() =>
            queryClient.invalidateQueries({ queryKey: ["allUsers"] })
          }
        />
      )}
    </div>
  );
};

export default AllUsers;
